import environments from './XEnvironment.js';
import { XTemplateDOM } from './XDOM.js';
import { xInteger, xObject, xReference, xString } from './XTypes.js';
import { xBase64ToObject, xObjectToBase64, xRandomString } from './XUtil.js';

import template from './XBlock.html?raw';

import { XBlockStructure } from './XBlockStructure.js';
import { XMediaVideo } from './XSection.js';

export class XBlock
{
    static get type () { return 'XBlock'; }

    #dom;
    #parent;
    #section;
    #content;
    #page;
    #site;

    #textVoice;
    #mediaID;
    #mediaType;
    #mediaData;

    #element;
    #elementAnchor;
    #elementMarker;
    #elementPlayButton;
    #elementStopButton;

    #media;
    #blockMedia = null;
    #blockEndAnchor = null;

    #id;
    #windowScrollPosition = 0;
    #top = 0;
    #height = 0;

    constructor(dom, parent, section, content, page, site)
    {
        this.#dom     = dom;
        this.#parent  = parent;
        this.#section = section;
        this.#content = content;
        this.#page    = page;
        this.#site    = site;

        this.#id = dom._element.dataset.xBlockId;

        if('xData' in dom._element.dataset)
        {
            const data = xBase64ToObject(dom._element.dataset.xData);
            this.#mediaType = data.mediaType;
            this.#mediaID = data.mediaID;
            if('mediaData' in data)
            {
                this.#mediaData = data.mediaData;
            }
        }

        const domTypes = environments.active.domTypes;
        this.#textVoice = this.#dom.content.textVoice._setCreator(model => new domTypes.XTextVoice(model, this, this.#content));
        this.#elementMarker = this.#dom.marker._element;
        
        this.onElementTypeChanged();
        this.updateBlockDepth();
    };

    updateBlockDepth()
    {
        for(const className of this.#elementMarker.classList)
        {
            if(className.startsWith('x-depth'))
            {
                this.#elementMarker.classList.remove(className);
                break;
            }
        }
        this.#elementMarker.classList.add('x-depth-' + String(this.blockDepth));
    }

    updateButtons()
    {
        this.#elementMarker.innerHTML = '';
        
        const mediaType = this.mediaType;
        this.#elementPlayButton = document.createElement('i');
        this.#elementPlayButton.classList.add('x-button', 'x-left', 'bi', 'bi-play-btn');
        this.#elementMarker.appendChild(this.#elementPlayButton);
        this.#elementPlayButton.addEventListener('click', () =>
        {
            this.play();
        });
        this.#elementStopButton = document.createElement('i');
        this.#elementStopButton.classList.add('x-button', 'x-left', 'bi', 'bi-stop-btn', 'd-none');
        this.#elementMarker.appendChild(this.#elementStopButton);
        this.#elementStopButton.addEventListener('click', () =>
        {
            this.stop();
        });
        if(mediaType)
        {
            const elementButton = document.createElement('i');
            elementButton.classList.add('x-button', 'x-right', 'bi');
            if(mediaType == 'title')
            {
                elementButton.classList.add('bi-list-nested');
            }
            else if(mediaType == 'image')
            {
                elementButton.classList.add('bi-image');
            }
            else if(mediaType == 'video')
            {
                elementButton.classList.add('bi-film');
            }
            this.#elementMarker.appendChild(elementButton);
        }
    }

    onElementTypeChanged()
    {
        this.#element = this.#dom._element;
        this.#element.addEventListener('click', (event) =>
        {
            this.scrollHere();
            if(event.shiftKey)
            {
                window.location.hash = this.id;
            }
        });
    }

    onResize(currentSourceScroll, topPositionInContext)
    {
        this.#top = topPositionInContext;
        this.#height = this.#element.getBoundingClientRect().height;
        this.#windowScrollPosition = currentSourceScroll + 10;
    }

    onMediaEnded()
    {
        const next = this.next;
        if(next)
        {
            next.play();
        }
        else
        {
            this.stop();
        }
    }

    get isPlaying()
    {
        return this.#content.blockPlayback == this;
    }

    set volume(volume)
    {
        this.textVoice.volume = volume;
        if(this.media instanceof XMediaVideo)
        {
            this.media.volume = volume;
        }
    }

    play({
        doNotSetCurrentTime = false
    } = {})
    {
        if(this.#content.blockPlayback == this)
        {
            return;
        }
        this.scrollHere();
        if(this.#content.blockPlayback)
        {
            this.#content.blockPlayback.stop({
                doNotStopMedia: this.media instanceof XMediaVideo && this.media == this.#content.blockPlayback.media
            });
        }
        this.#content.blockPlayback = this;
        if(this.#blockMedia && this.hasHighlightFocus)
        {
            this.#blockMedia.setHighlightMedia(true);
        }
        this.#element.classList.add('x-highlight-playback');
        this.#elementPlayButton.classList.add('d-none');
        this.#elementStopButton.classList.remove('d-none');
        if(this.media instanceof XMediaVideo)
        {
            const timestamp = this.mediaData?.timestamp ?? 0;
            if(!this.media.isPlaying)
            {
                this.media.setCurrentTime(timestamp);
                this.media.play();
            }
            else if(!doNotSetCurrentTime)
            {
                this.media.setCurrentTime(timestamp);
            }
            if(!this.media.hasAudio)
            {
                if(!this.textVoice.isPlaying)
                {
                    this.textVoice.play();
                }
            }
        }
        else
        {
            this.textVoice.play().then((ended) => 
            {
                if(ended)
                {
                    this.onMediaEnded();
                }
            }).catch(() =>
            {
                this.stop();
            });
        }
    }
    
    stop({
        doNotStopMedia = false
    } = {})
    {
        this.#element.classList.remove('x-highlight-playback');
        this.#elementPlayButton.classList.remove('d-none');
        this.#elementStopButton.classList.add('d-none');

        this.textVoice.stop();
        if(!doNotStopMedia)
        {
            if(this.media instanceof XMediaVideo)
            {
                this.media.pause();
            }
        }
        this.#content.blockPlayback = null;
    }

    get hasHighlightFocus()
    {
        return this.#section.blockFocus == this;
    };

    setHighlightFocus(highlight)
    {
        if(highlight)
        {
            if(this.#section.blockFocus != this)
            {
                this.#section.unhighlightFocusBlocks();
                this.#element.classList.add('x-highlight-focus');
                if(this.#blockMedia)
                {
                    this.#blockMedia.setHighlightMedia(true);
                }
                this.#section.blockFocus = this;
                this.#content.blockFocus = this;
            }
        }
        else
        {
            if(this.#section.blockFocus == this)
            {
                this.#element.classList.remove('x-highlight-focus');
                this.#section.blockFocus = null;
            }
        }
    };

    setHighlightMedia(highlight)
    {
        if(!this.#media)
        {
            return;
        }
        if(highlight)
        {
            this.section.unhighlightMediaBlocks();
            this.#element.classList.add('x-highlight-media');
            this.#media.show(this);
        }
        else
        {
            this.#element.classList.remove('x-highlight-media');
        }
    }

    scrollHere(instant=false)
    {
        this.#section.updateMedia(true);
        window.scrollTo({ 
            left     : 0, 
            top      : this.#windowScrollPosition, 
            behavior : instant ? 'instant' : 'smooth'
        });
    }

    get isVisible()
    {
        if(!this.#blockEndAnchor)
        {
            return false;
        }
        const top = this.#element.getBoundingClientRect().top;
        const bottom = this.#blockEndAnchor.element.getBoundingClientRect().bottom;
        const boundingClientRectText = this.#section.elementText.getBoundingClientRect();
        return bottom > boundingClientRectText.top && bottom > this.#page.navbarHeight && top < boundingClientRectText.bottom && top < this.#page.windowHeight;
    }

    get previous()
    {
        const blocksFlattened = this.#section.blocksFlattened;
        const index = blocksFlattened.indexOf(this);
        if(index < 0)
        {
            return undefined;
        }
        const previousIndex = index - 1;
        if(previousIndex >= 0)
        {
            return blocksFlattened[previousIndex];
        }
        const previousSection = this.#section.previous;
        if(!previousSection)
        {
            return undefined;
        }
        return previousSection.blocksFlattened[previousSection.blocksFlattened.length-1];
    }

    get next()
    {
        const blocksFlattened = this.#section.blocksFlattened;
        const index = blocksFlattened.indexOf(this);
        if(index < 0)
        {
            return undefined;
        }
        const nextIndex = index + 1;
        return nextIndex < blocksFlattened.length ? blocksFlattened[nextIndex] : this.#section.next?.blocksFlattened[0];
    }

    calculateBlocksFlattened()
    {
        return [this];
    }

    get blockDepth()
    {
        return this.#parent.blockDepth;
    }

    set elementAnchor  (elementAnchor ) { this.#elementAnchor  = elementAnchor;  }
    set media          (media         ) { this.#media          = media;          }
    set blockMedia     (blockMedia    ) { this.#blockMedia     = blockMedia;     }
    set blockEndAnchor (blockEndAnchor) { this.#blockEndAnchor = blockEndAnchor; }

    get _dom                 () { return this.#dom;                  }
    get parent               () { return this.#parent;               }
    get section              () { return this.#section;              }
    get content              () { return this.#content;              }
    get page                 () { return this.#page;                 }
    get site                 () { return this.#site;                 }
    get textVoice            () { return this.#textVoice.mount;      }
    get media                () { return this.#media;                }
    get mediaID              () { return this.#mediaID;              }
    get mediaType            () { return this.#mediaType;            }
    get mediaData            () { return this.#mediaData;            }
    get element              () { return this.#element;              }
    get elementAnchor        () { return this.#elementAnchor;        }
    get blockMedia           () { return this.#blockMedia;           }
    get blockEndAnchor       () { return this.#blockEndAnchor;       }
    get id                   () { return this.#id;                   }
    get windowScrollPosition () { return this.#windowScrollPosition; }
    get top                  () { return this.#top;                  }
    get height               () { return this.#height;               }
};

export class XBlockEditorDecorator
{
    #block;

    constructor(block)
    {
        this.#block = block;

        if(this.#block.site.isEditor)
        {
            this.#block._dom.editor?.addAfter._element.addEventListener('click', (event) => 
            {
                this.addNewAfter(this.#block.parent instanceof XBlockStructure ? 'li' : undefined);
                event.stopPropagation();
            });
            this.#block._dom.editor?.addBlockStructureAfter._element.addEventListener('click', (event) => 
            {
                this.addNewBlockStructureAfter();
                event.stopPropagation();
            });
        }
        if(this.#block.site.isTranslator)
        {
            this.#block._dom.editor._destroy();
            delete this.#block._dom.editor;
        }
    }

    index()
    {
        return this.#block.parent.blocks?.indexOf(this.#block);
    }

    remove()
    {
        const index = this.#block.parent.blocks.indexOf(this.#block);
        if(index > -1)
        {
            this.#block.parent.data.blocks.splice(index, 1);
            return true;
        }
        return false;
    }

    addNewAfter(type)
    {
        const index = this.index();
        const block = this.createNewData(type);
        this.#block.parent.data.blocks.splice(index+1, 0, block._key);
    }

    addNewBlockStructureAfter()
    {
        const index = this.index();
        const blockStructure = this.createNewDataInBlockStructure();
        this.#block.parent.data.blocks.splice(index+1, 0, blockStructure._key);
    }

    createNewDataInBlockStructure()
    {
        const blockStructure = this.#block.site.storage.addData('XBlockStructure');
        const block = this.createNewData('li');
        blockStructure.blocks.push(block._key);
        return blockStructure;
    }

    createNewData(type)
    {
        const block = this.#block.site.storage.addData('XBlock');
        const textVoice = this.#block.site.storage.addData('XTextVoice');
        block.textVoice = textVoice._key;
        textVoice.text = 'NEW BLOCK';
        if(type)
        {
            block.type = type;
        }
        return block;
    }
}

console.log(xRandomString(12));
console.log(xRandomString(12));
console.log(xRandomString(12));
console.log(xRandomString(12));
console.log(xRandomString(12));
console.log(xRandomString(12));
console.log(xRandomString(12));

export class XBlockReactive extends XBlock
{
    static get specification () 
    { 
        return xObject({
            $: XBlock.type,
            type: xString('p'),
            style: xString(''),
            mediaType: xString(),
            media: xReference(['XImage', 'XVideo']),
            mediaBaked: xReference(['XImage', 'XVideo']),
            timestamp: xInteger(),
            textVoice: xReference('XTextVoice'),
            comment: xString(),
        });
    }

    static #template;

    static initDOM(domEnvironment)
    {
        this.#template = new XTemplateDOM(template, XBlockReactive.specification, domEnvironment);
    }

    #data;
    #mediaID;

    #blockEditor;

    constructor(data, parent, section, content, page, site)
    {
        const dom = XBlockReactive.#template._createAccessibleDOM(data);
        dom._element.dataset.xBlockId = data._key;
        super(dom, parent, section, content, page, site);
        this.#data = data;

        this.#blockEditor = new XBlockEditorDecorator(this);
        
        this.textVoice?.elementText.addEventListener('blur', () => 
        {
            if(this.mediaType == 'title')
            {
                this.section.updateMedia();
            }
            this.page.updateAnchors();
        });
        
        if(this.site.isEditor)
        {
            this._dom.editor.forceTextToSpeech._element.addEventListener('click', () =>
            {
                environments.active.api.createVoice(this.#data.textVoice, this.site.storage.locale, true).then(() => 
                {
                    this.textVoice.play();
                });
            });
            this._dom.editor.timestamp._element.addEventListener('input', (event) => 
            {
                event.target.value = event.target.value.replace(/\D/g, '');
                this.section.updateMedia();
                event.stopPropagation();
            });
            this._dom.editor.timestamp._element.addEventListener('paste', (event) =>
            {
                const pastedData = (event.clipboardData || window.clipboardData).getData('text');
                let numberValue = parseInt(pastedData);
            
                if(!isNaN(numberValue)) 
                {
                    event.target.value = numberValue;
                }
                event.preventDefault();
            });
            this._dom.editor.fontLineHeight._element.addEventListener('input', (event) => 
            {
                this.#data.media.fontLineHeight = parseFloat(event.target.value);
                this.section.updateMedia();
                event.stopPropagation();
            });
            this._dom.editor.import._element.addEventListener('click', () =>
            {
                if(this.mediaType == 'image' || this.mediaType == 'title')
                {
                    let data = this.#data.media;
                    if(!data)
                    {
                        data = this.site.storage.addData('XImage');
                        data.name = this.site.storage.localeFallback;
                        this.#data.media = data._key;
                    }
                    if(this.site.storage.locale != this.site.storage.localeFallback)
                    {
                        data.name_._markAsLocalized();
                        data._updateStorageData();
                        data.name = this.site.storage.locale;
                    }
                    environments.active.api.importImage(data).then(() => {
                        this.#updateMediaData();
                        this.section.updateMedia();
                        this.scrollHere();
                    });
                }
                else if(this.mediaType == 'video')
                {
                    let data = this.#data.media;
                    if(!data)
                    {
                        data = this.site.storage.addData('XVideo');
                        data.name = this.site.storage.localeFallback;
                        this.#data.media = data._key;
                    }
                    if(this.site.storage.locale != this.site.storage.localeFallback)
                    {
                        data.name_._markAsLocalized();
                        data._updateStorageData();
                        data.name = this.site.storage.locale;
                    }
                    environments.active.api.importVideo(data).then(() => {
                        this.#updateMediaData();
                        this.section.updateMedia();
                        this.scrollHere();
                    });
                }
            });
            this._dom.editor.mediaType._element.addEventListener('input', () =>
            {
                this.#updateMediaData();
                this.section.updateMedia();
            });
            this._dom.editor.media._element.addEventListener('input', () =>
            {
                this.#updateMediaData();
                this.section.updateMedia();
            });
        }

        this.#updateMediaData();

        this._dom._attachData(this.#data.style_, {
            _setValue: (value) => {
                this._dom.content._element.classList.remove('x-text-note');
                if(value == 'note')
                {
                    this._dom.content._element.classList.add('x-text-note');
                }
            }
        });

        this._dom._attachData(this.#data.type_, {
            _setValue: (value, oldType) => {
                let type = value.valueOf();
                if(type != oldType)
                {
                    if(oldType == 'li')
                    {
                        this._dom.content._unwrapElements();
                    }
                    if(type == 'li')
                    {
                        this._dom.content._wrapElements('li');
                        type = 'span';
                    }
                    this._dom._changeElementType(type);
                    this.onElementTypeChanged();
                    this.page.updateAnchors();
                }
            }
        });
    };

    #updateMediaData()
    {
        this._dom.editor?.timestamp._element.classList.add('d-none');
        this._dom.editor?.fontLineHeight._element.classList.add('d-none');
        this._dom.editor?.media._element.classList.add('d-none');
        this._dom.editor?.mediaBaked._element.classList.add('d-none');
        if(this.mediaType == 'image')
        {
            this.#mediaID = this.#data.media_._key;
            this._dom.editor?.media._element.classList.remove('d-none');
            this._dom.editor?.fontLineHeight._element.classList.remove('d-none');
        }
        else if(this.mediaType == 'video')
        {
            this.#mediaID = this.#data.media_._key;
            this._dom.editor?.timestamp._element.classList.remove('d-none');
            this._dom.editor?.media._element.classList.remove('d-none');
            this._dom.editor?.mediaBaked._element.classList.remove('d-none');
        }
        else if(this.mediaType == 'title')
        {
            this.#mediaID = xRandomString(12);
            this._dom.editor?.media._element.classList.remove('d-none');
        }
        else
        {
            this.#data.media = '';
            this.#mediaID = null;
        }
    }

    collectStaticStorageIDs(collectedIDs)
    {
        if(this.#data.media)
        {
            collectedIDs.add(this.#data.mediaBaked ? this.#data.mediaBaked_._key : this.#data.media_._key);
        }
    }

    _render(dom)
    {
        if(this.mediaType)
        {
            const data = {
                mediaType: this.mediaType,
                mediaID: this.#mediaID,
            };
            const mediaData = this.mediaData;
            if(mediaData)
            {
                data.mediaData = mediaData;
            }
            if(this.#data.mediaBaked)
            {
                if(this.mediaType == 'title')
                {
                    mediaData.imageID = this.#data.mediaBaked_._key;
                }
                else
                {
                    data.mediaID = this.#data.mediaBaked_._key;
                }
            }
            dom._element.dataset.xData = xObjectToBase64(data);
        }
    }

    get mediaData ()
    {
        if(this.mediaType == 'video')
        {
            return {
                timestamp: this.#data.timestamp / 1000
            };
        }
        else if(this.mediaType == 'title')
        {
            return {
                text: this.#data.textVoice.text,
                imageID: this.#data.media_._key
            };
        }
    }

    get data      () { return this.#data;             }
    get mediaID   () { return this.#mediaID;          }
    get mediaType () { return this.#data.mediaType;   }
};
